import { css } from '@emotion/react'
import { Button, Card, colors, font, mq } from 'plume-ui'
import { useState, useEffect } from 'react'
import { fetchCompanyChart } from '@/lib/api/company'
import { isIPO } from '@/lib/company'
import { formatDate } from '@/lib/date'
import { printError } from '@/lib/log'
import { formatWithComma, formatAmount } from '@/lib/number'
import {
  ChartFundingRoundDesc,
  CompanyChartData,
  CompanyChartEventData,
  CompanyChartNewsData,
  CompanyData,
  InitialSeries,
} from '../../../../types/api'
import { A } from '../../../atoms/A'
import { Modal, ModalProps } from '../../../atoms/Modal'
import { EmptyNumber } from './EmptyNumber'
import {
  colorOfInitialSeries,
  colorOfRound,
} from './FundingRoundAndEmployeeTransitionChart'
import Link from 'next/link'
import { IcomoonIcon } from '@/components/atoms/IcomoonIcon'
import { loginPath, trialRequestUrl } from '@/lib/url'

export const GuestBasicInfo: React.FC<{
  company: CompanyData
}> = (props) => {
  const [data, setData] = useState<CompanyChartData | null>()

  useEffect(() => {
    setData(null)

    fetchCompanyChart(props.company.shortname)
      .then((result) => setData(result))
      .catch((e) => printError(e))
  }, [props.company.shortname])

  return (
    <>
      <BasicFinanceInfo company={props.company} data={data!} />
      <GrowthTransition />
    </>
  )
}

const BasicFinanceInfo: React.FC<{
  company: CompanyData
  data: CompanyChartData | null | undefined
}> = (props) => {
  const formattedValuation = props.company.valuation
    ? formatAmount(props.company.valuation, {
        estimated: props.company.hasEstimatedRound,
      })
    : ''

  return (
    <Card
      css={css`
        border-top: 1px solid ${colors.border};
        border-bottom: 1px solid ${colors.border};
      `}
    >
      <div
        css={css`
          display: flex;
          padding: 35px 25px;
          justify-content: space-between;
          ${mq.untilMedium} {
            flex-direction: column;
          }
        `}
      >
        <div
          css={css`
            flex-basis: 35%;
          `}
        >
          {isIPO(props.company) ? (
            <FinanceInfoItem
              label="IPO時 時価総額"
              labelAnnotation="（初値）"
              value={formatWithComma(props.company.ipoData?.ipoMarketCap)}
              unit={'百万円'}
            />
          ) : (
            <FinanceInfoItem
              label="調達後評価額"
              labelAnnotation="（潜在株を含む）"
              value={formattedValuation ? formattedValuation.value : ''}
              unit={formattedValuation ? formattedValuation.unit : ''}
              valueAnnotationDesc="算出ラウンド"
              valueAnnotationDate={
                props.company.valuation &&
                props.company.financeBasicInfo?.lastValuationDate
                  ? formatDate(
                      props.company.financeBasicInfo.lastValuationDate,
                      'yyyy/MM/dd'
                    )
                  : null
              }
            />
          )}
          <FinanceInfoItem
            label={'総調達額'}
            value={''}
            unit={''}
            css={css`
              margin-top: 20px;
            `}
          />
          <FinanceInfoItem
            label="最新ラウンド調達額"
            value={''}
            unit={''}
            css={css`
              margin-top: 20px;
            `}
          />
        </div>
        <div
          css={css`
            margin-right: 200px;
            ${mq.untilMedium} {
              margin-right: 0;
            }
          `}
        >
          <div>
            <div
              css={css`
                color: ${colors.textStrong};
                font-weight: ${font.weight.bold};
                font-size: 14px;
              `}
            >
              総調達額の内訳
              <span
                css={css`
                  font-weight: ${font.weight.normal};
                  font-size: 12px;
                  margin-left: 4px;
                `}
              >
                （過去分含む）
              </span>
            </div>
            <div
              css={css`
                margin-top: 8px;
                ${mq.untilMedium} {
                  text-align: center;
                }
              `}
            >
              <img src="/static/images/companies/guest/startup-blur-chart1.png" />
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}

const FinanceInfoItem: React.FC<{
  label: string
  labelAnnotation?: string
  value: string
  unit: string
  valueAnnotationDesc?: string
  valueAnnotationDate?: string | null
  className?: string
}> = (props) => {
  return (
    <div
      css={css`
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
      `}
      className={props.className}
    >
      <div
        css={css`
          flex: 1;
        `}
      >
        <div
          css={css`
            color: ${colors.textStrong};
            font-weight: ${font.weight.bold};
            font-size: 14px;
            margin-top: 5px;
          `}
        >
          {props.label}
        </div>
        <div
          css={css`
            color: ${colors.textLight};
            font-size: 13px;
            margin-top: 5px;
          `}
        >
          {props.labelAnnotation}
        </div>
      </div>
      <div
        css={css`
          flex: 1;
        `}
      >
        <div
          css={css`
            font-size: 20px;
          `}
        >
          {!props.value ? (
            <img src="/static/images/companies/guest/startup-blur-text1.png" />
          ) : (
            <>
              {props.value || <EmptyNumber />}
              <span
                css={css`
                  font-size: 13px;
                  color: ${colors.textLight};
                  margin-left: 5px;
                `}
              >
                {props.unit}
              </span>
            </>
          )}
        </div>
        {props.valueAnnotationDate ? (
          <div
            css={css`
              font-size: 13px;
              color: ${colors.textLight};
              margin-top: 5px;
            `}
          >
            {props.valueAnnotationDesc}：
            <span
              css={css`
                font-size: 14px;
                color: ${colors.textLight};
              `}
            >
              {props.valueAnnotationDate}
            </span>
          </div>
        ) : (
          <div
            css={css`
              width: 166px;
            `}
          />
        )}
      </div>
    </div>
  )
}

const GrowthTransition: React.FC<{}> = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  return (
    <>
      <Card>
        <div
          css={css`
            padding: 36px 25px;
          `}
        >
          <div
            css={css`
              font-weight: ${font.weight.bold};
              font-size: 15px;
              line-height: 1.46;
            `}
          >
            成長の推移
            <span onClick={() => setIsModalOpen(true)}>
              <IcomoonIcon
                name="question-circle-fill"
                css={css`
                  font-size: 15px;
                  padding-left: 3px;
                  cursor: pointer;
                `}
              />
            </span>
          </div>
          <div
            css={css`
              margin-top: 16px;
              display: flex;
              flex-direction: column;
              align-items: center;
              background: url('/static/images/companies/guest/growth-history-chart.png');
              background-position: center center;
              background-size: cover;
              background-repeat: no-repeat;
              padding: 150px 0 230px;
              ${mq.untilMedium} {
                display: none;
              }
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                max-width: 480px;
                font-weight: ${font.weight.normal};
                font-size: 14px;
                line-height: 175%;
                letter-spacing: 0.02em;
                color: ${colors.text};
              `}
            >
              <p
                css={css`
                  margin: 0;
                  text-align: center;
                `}
              >
                有料契約限定のコンテンツです。
                <br />
                無料トライアルに申し込むと、すべてのコンテンツをご覧になれます。
              </p>
              <Link href={trialRequestUrl} passHref>
                <a target="_blank">
                  <Button
                    css={css`
                      margin-top: 19px;
                    `}
                  >
                    無料トライアルに申し込む
                  </Button>
                </a>
              </Link>
              <A
                decoration="none"
                href={loginPath}
                css={css`
                  margin-top: 23px;
                `}
              >
                契約済みの方はログインしてください
              </A>
            </div>
          </div>
          <div>
            <p
              css={css`
                margin: 0;
                margin-block-start: 16px;
                display: flex;
                flex-direction: column;
                align-items: center;
                font-weight: ${font.weight.normal};
                font-size: 14px;
                line-height: 175%;
                letter-spacing: 0.02em;
                color: ${colors.text};
                ${mq.large} {
                  display: none;
                }
              `}
            >
              <img src="/static/images/companies/guest/growth-history-chart-mobile.png" />
              <span
                css={css`
                  text-align: center;
                  display: block;
                `}
              >
                有料契約限定のコンテンツです。
                <br />
                無料トライアルに申し込むと、すべてのコンテンツをご覧になれます。
              </span>
              <Link href={trialRequestUrl} passHref>
                <a
                  target="_blank"
                  css={css`
                    width: 100%;
                  `}
                >
                  <Button
                    css={css`
                      margin-top: 16px;
                    `}
                    fullwidth
                  >
                    無料トライアルに申し込む
                  </Button>
                </a>
              </Link>
              <A
                decoration="none"
                href={loginPath}
                css={css`
                  margin-top: 20px;
                `}
              >
                契約済みの方はログインしてください
              </A>
            </p>
          </div>
        </div>
      </Card>
      <GrowthTransitionModal
        isHidden={!isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  )
}

const GrowthTransitionModal: React.FC<ModalProps> = (props) => {
  return (
    <Modal
      {...props}
      css={css`
        max-width: 900px;
        .modal-content {
          padding: 55px 50px;
        }
      `}
    >
      <div>
        <ModalTitle
          css={css`
            margin-top: 0;
          `}
        >
          成長の推移
        </ModalTitle>
        <div>
          {FUNDING_ROUND_TYPE_LIST.map((types, i) => (
            <div
              key={i}
              css={css`
                display: flex;
                margin-top: 16px;
              `}
            >
              <span
                css={css`
                  background-color: ${colorOfRound(types[0])};
                  width: 18px;
                  height: 18px;
                  border-radius: 50%;
                  margin-right: 10px;
                  min-width: 18px;
                `}
              />
              <div
                css={css`
                  line-height: 1.4;
                `}
              >
                {types.join('、')}
              </div>
            </div>
          ))}
        </div>
        <ModalTitle>スピーダ調達シリーズ</ModalTitle>
        <div>
          スピーダの定義に基づいたシリーズ。定義については
          <A
            href={process.env.INITIAL_SERIES_HELP_ENDPOINT}
            target="_blank"
            decoration="none"
          >
            こちら
          </A>
          <div
            css={css`
              display: flex;
              margin-top: 12px;
            `}
          >
            {SERIES_LIST.map((s, i) => (
              <div
                key={i}
                css={css`
                  height: 42px;
                  width: 115px;
                  border: 1px solid ${colors.black10};
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  background-color: ${colorOfInitialSeries(s)};
                  &:not(:first-of-type) {
                    border-left: 0;
                  }
                `}
              >
                {s}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div>
        <ModalTitle>イベント</ModalTitle>
        <div>{EVENT_TYPE_LIST.join('、')}</div>
      </div>
      <div>
        <ModalTitle>ニュース</ModalTitle>
        <div
          css={css`
            line-height: 1.5;
          `}
        >
          {NEWS_TYPE_LIST.join('、')}
        </div>
      </div>
    </Modal>
  )
}

type ModalTitleProps = {
  className?: string
  children: React.ReactNode
}

const ModalTitle: React.FC<ModalTitleProps> = (props) => {
  return (
    <div
      css={css`
        font-size: 18px;
        line-height: 1.5;
        font-weight: ${font.weight.bold};
        margin-top: 25px;
        margin-bottom: 5px;
      `}
      className={props.className}
    >
      {props.children}
    </div>
  )
}

const FUNDING_ROUND_TYPE_LIST: ChartFundingRoundDesc[][] = [
  ['有償第三者割当', '有償株主割当'],
  [
    '公募増資',
    '増資',
    '資本組み入れ',
    '現金出資・現物出資',
    'クラウドファンディング',
  ],
  ['調達方法不明', '短期借入(Loan)', '長期借入(Debt)'],
  ['創業', '会社設立', '合併（M&A,事業継承など）'],
  [
    '新株予約権の付与（発行）',
    '新株引受権付社債の発行',
    '新株予約権付社債の発行',
    '転換社債型新株予約権付社債の発行',
    '転換社債の発行',
    '転換ローン(Convertible Note)の発行',
    '転換ローン(Convertible Note)の発行 (日本版)',
    '新株引受権の発行',
  ],
  [
    '新株予約権の権利行使',
    '新株引受権付社債の権利行使',
    '新株予約権付社債の権利行使',
    'グリーンシート',
    '転換社債型新株予約権付社債の権利行使',
    '転換社債の株式転換',
    '転換ローン(Convertible Note)の株式転換',
    '転換ローン(Convertible Note)の株式転換 (日本版)',
    '新株引受権の権利行使',
  ],
  ['株式の分割', '株式の移動'],
  ['不明'],
]

const SERIES_LIST: InitialSeries[] = [
  'シード',
  'シリーズA',
  'シリーズB',
  'シリーズC',
  'シリーズD',
  'シリーズE',
  'シリーズF',
  'シリーズG',
]

const EVENT_TYPE_LIST: CompanyChartEventData['type'][] = [
  '創業',
  '会社設立',
  '合併（M&A,事業継承など）',
  '被買収・子会社化・主要株式取得',
  '事業提携',
  '事業譲渡',
  '事業譲受',
  '破綻・清算・解散',
  '住所変更',
  'IPO',
]

const NEWS_TYPE_LIST: CompanyChartNewsData['type'][] = [
  'ファイナンス・M&A',
  '外部ニュース',
]
